import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react';
import {Button, Modal, Tab, Tabs} from "react-bootstrap";
import Loading from "../../components/loading";
import {Form, Formik} from "formik";
import {userRequestSchema, userUserGroupRoleRequest} from "../../utils/schemas";
import FormGroup from "../../components/form-group";
import HelperService from "../../services/helper-service";
import Section from "../../components/section";
import {useAuth} from "../../context/auth";
import {useUserService} from "../../services/user-service";
import {useGlobalNotificationMessage} from "../../context/message";
import {useRoleService} from "../../services/role-service";
import Fileupload from "../../components/files/fileupload";
import UserGroupsSelect from "../user-groups/user-groups-select";
import AddEditUserGroupModal from "../user-groups/add-edit-user-group-modal";
import {useUserGroupService} from "../../services/user-group-service";
import {AppRoleTypes, LocaleList, UserAttachmentTypes, UserTypes} from "../../utils/constants";
import AddressCell from "../../components/address-cell";
import ContactCell from "../../components/contact-cell";
import AddEditContactModal from "./add-edit-contact-modal";
import AddEditAddressModal from "./add-edit-address-modal";
import {useCountryService} from "../../services/country-service";
import {useTimezoneService} from "../../services/timezone-service";
import {useAddressService} from "../../services/address-service";
import {useContactService} from "../../services/contact-service";
import AddEditBankAccountModal from "./add-edit-bank-account-modal";
import {useBankAccountService} from "../../services/bank-account-service";
import BankAccountCell from "../../components/bank-account-cell";
import AddEditUserRepresentativeModal from "./add-edit-user-representative-modal";
import {useUserRepresentativeService} from "../../services/user-representative-service";
import UserRepresentativeCell from "../../components/user-representative-cell";
import Attachment from "../../components/files/attachment";
import AddEditAttachmentModal from "../attachment/add-edit-attachment-modal";
import {useAttachmentCategoryService} from "../../services/attachment-category-service";

const AddEditUserModal = forwardRef( ({onSuccess=null, ...props}, ref) => {
  const [show, setShow] = useState(false);
  const [model, setModel] = useState();
  const [syncUserGroup, setSyncUserGroup] = useState(userUserGroupRoleRequest.cast());
  const [files, setFiles] = useState([]);
  const [filesData, setFilesData] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [userGroupsList, setUserGroupsList] = useState();
  const [countriesList, setCountriesList] = useState([]);
  const [timezonesList, setTimezonesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const {addErrorMessage, removeMessage } = useGlobalNotificationMessage();
  const [allowedAttachmentCategories, setAllowedAttachmentCategories] = useState([]);
  const [reset, setReset] = useState(0);

  const addEditUserGroupModal = useRef();
  const addEditContactModal = useRef();
  const addEditAddressModal = useRef();
  const addEditBankAccountModal = useRef();
  const addEditUserRepresentativeModal = useRef();
  const addEditAttachmentModal = useRef();

  let formSchema = userRequestSchema;

  let auth = useAuth();
  let userService = useUserService();
  let roleService = useRoleService();
  let userGroupService = useUserGroupService();
  let countryService = useCountryService();
  let timezoneService = useTimezoneService();
  let addressService = useAddressService();
  let contactService = useContactService();
  let bankAccountService = useBankAccountService();
  let userRepresentativeService = useUserRepresentativeService();
  let attachmentCategoryService = useAttachmentCategoryService();

  //expose api for interacting with component
  useImperativeHandle(ref, () => ({
    openModal: openModal,
    closeModal: closeModal,
  }));

  const openModal = (model=null) => {
    setLoading(true);
    setFiles([]);

    let promises = [
      roleService.getList(),
      userGroupService.getList(),
      countryService.getList(),
      timezoneService.getList(),
      attachmentCategoryService.getList()
    ];

    if (model && model.id){
      promises.push(getDetail(model.id));
    }

    Promise.all(promises).then(([roles=[], userGroups=[], countries=[], timezones=[], attachmentCategories = [], detail= {}])=>{
      setRolesList(roles);
      setUserGroupsList(userGroups);
      setCountriesList(countries);
      setAllowedAttachmentCategories(attachmentCategories);
      setTimezonesList(Object.values(timezones));

      let newDetail = {};

      //user detail
      if (detail){
        //attach new role
        detail.new_role_id = detail.global_roles && detail.global_roles.length ? detail.global_roles[0].id : null;

        //sync group - for now only one group per user
        setSyncUserGroup(()=>{
          return userUserGroupRoleRequest.cast(detail.user_groups && detail.user_groups.length>0 ? detail.user_groups[0].pivot : {} );
        });
      }

      newDetail = Object.assign(
        {},
        {type: UserTypes.person.val},
        formSchema.cast(detail)
      );

      setModel(formSchema.cast(newDetail));
      setLoading(false);
    });

    setShow(true);
  }
  const closeModal = () => {
    setShow(false);
    setReset(reset+1);
    setModel(formSchema.cast());
  }

  const getDetail = (id) => {
    return userService.getDetail(id);
  }

  const onAddEditUserGroupAction = (row=null, e=null)=>{
    addEditUserGroupModal.current.openModal(row);
  };

  const onAddEditUserGroupCallback = (item)=>{
    setUserGroupsList((userGroupsList)=>{
      return [...HelperService.addOrReplaceItemInList(userGroupsList, item)];
    })
  };

  const onAddEditAttachmentCallback = (item)=>{
    setModel((model)=>{
      let m = model;
      m.attachments = [...HelperService.addOrReplaceItemInList(m.attachments, item)];
      return Object.assign({}, m);
    })
  };

  const onAttachmentDelete = (item)=>{
    setModel((model)=>{
      let m = model;
      m.attachments = [...HelperService.removeItemFromList(m.attachments, item)];
      return Object.assign({}, m);
    })
  };

  const onSubmit = (values, { setSubmitting, setErrors }) => {
    removeMessage();
    let reqValues = {...values};
    if (files && files.length){
      reqValues.files = files;
    }

    if (syncUserGroup.role_id && syncUserGroup.user_group_id){
      reqValues.sync_user_groups = [syncUserGroup];
    }

    delete reqValues.versions;
    delete reqValues.version_diffs;
    delete reqValues.user_groups;
    delete reqValues.global_roles;
    delete reqValues.attachments;
    delete reqValues.contacts;
    delete reqValues.addresses;
    delete reqValues.user_representatives;

    userService.addEditModel(reqValues, reqValues.id)
      .then((res) => {
        if (auth.currentUser.id === res.id){
          auth.setCurrentUser(res);
        }
        if (onSuccess && 'function' === typeof onSuccess){
          onSuccess(res);
        }
        setFiles([]);
        closeModal();
      })
      .catch(error => {
        HelperService.handleApiErrors(error, addErrorMessage, "Nepodarilo sa uložiť zmeny.");
        HelperService.handleFormErrors(error, setErrors);
      })
      .finally(()=>{ setSubmitting(false);});
  };

  const onUserGroupSelectChange = (ug={}) => {
    setSyncUserGroup(userUserGroupRoleRequest.cast(ug));
  }

  const onAddEditContactCallback = (item)=>{
    let newModel = {...model};
    newModel.contacts = [...HelperService.addOrReplaceItemInList(newModel.contacts, item)];
    setModel(newModel);
  };

  const onAddEditUserRepresentativeCallback = (item)=>{
    let newModel = {...model};
    newModel.user_representatives = [...HelperService.addOrReplaceItemInList(newModel.user_representatives, item)];
    setModel(newModel);
  };

  const onAddEditAddressCallback = (item)=>{
    let newModel = {...model};
    newModel.addresses = [...HelperService.addOrReplaceItemInList(newModel.addresses, item)];
    setModel(newModel);
  };

  const onAddEditBankAccountCallback = (item)=>{
    let newModel = {...model};
    newModel.bank_accounts = [...HelperService.addOrReplaceItemInList(newModel.bank_accounts, item)];
    setModel(newModel);
  };

  const delAddress = (item) => {
    if (true===confirm('Odstrániť adresu?')){
      addressService.delModel(item.id)
        .then(()=>{
          let newModel = {...model};
          newModel.addresses = [...HelperService.removeItemFromList(newModel.addresses, item)];
          setModel(newModel);
        })
        .catch(error => {
          HelperService.handleApiErrors(error, addErrorMessage);
        })
    }
  };

  const delContact = (item) => {
    if (true===confirm('Odstrániť kontakt?')){
      contactService.delModel(item.id)
        .then(()=>{
          let newModel = {...model};
          newModel.contacts = [...HelperService.removeItemFromList(newModel.contacts, item)];
          setModel(newModel);
        })
        .catch(error => {
          HelperService.handleApiErrors(error, addErrorMessage);
        })
    }
  };

  const delBankAccount = (item) => {
    if (true===confirm('Odstrániť bankový účet?')){
      bankAccountService.delModel(item.id)
        .then(()=>{
          let newModel = {...model};
          newModel.bank_accounts = [...HelperService.removeItemFromList(newModel.bank_accounts, item)];
          setModel(newModel);
        })
        .catch(error => {
          HelperService.handleApiErrors(error, addErrorMessage);
        })
    }
  };

  const delUserRepresentative = (item) => {
    if (true===confirm('Odstrániť delegáta?')){
      userRepresentativeService.delModel(item.id)
        .then(()=>{
          let newModel = {...model};
          newModel.user_representatives = [...HelperService.removeItemFromList(newModel.user_representatives, item)];
          setModel(newModel);
        })
        .catch(error => {
          HelperService.handleApiErrors(error, addErrorMessage);
        })
    }
  };

  return (
    <>
      <AddEditAttachmentModal ref={addEditAttachmentModal} onSuccess={(item)=>onAddEditAttachmentCallback(item)} />
      <AddEditUserGroupModal ref={addEditUserGroupModal} onSuccess={(role)=>onAddEditUserGroupCallback(role)} />
      <AddEditContactModal ref={addEditContactModal} onSuccess={(item)=>onAddEditContactCallback(item)} />
      <AddEditAddressModal ref={addEditAddressModal} onSuccess={(item)=>onAddEditAddressCallback(item)} />
      <AddEditBankAccountModal ref={addEditBankAccountModal} onSuccess={(item)=>onAddEditBankAccountCallback(item)} />
      <AddEditUserRepresentativeModal ref={addEditUserRepresentativeModal} onSuccess={(item)=>onAddEditUserRepresentativeCallback(item)} />

      <Formik
        key={reset}
        enableReinitialize={true}
        initialValues={formSchema.cast(model) || formSchema.cast()}
        validationSchema={formSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched, handleSubmit, setFieldValue}) => (

          <Modal size="xl" show={!!show} onHide={closeModal} centered>
            <Modal.Header closeButton>
              { model && <Modal.Title>{ !model.id ? 'Nový používateľ' : 'Úprava používatela' }</Modal.Title>}
            </Modal.Header>
            <Modal.Body>
              <Section animation={HelperService.getRandomAnimation()}>

                {loading && <Loading className="m-2" />}

                {!loading && <Form className="row">

                  <Tabs
                    defaultActiveKey="info"
                    className="mb-3"
                    onSelect={(key)=> key==='asset-values' ? reloadBuildingStats() : null}
                  >

                    <Tab eventKey="info" title="Info">
                      <div className="row">
                        <FormGroup
                          name="type"
                          type="select"
                          options={Object.values(UserTypes)}
                          optionLabel="title"
                          optionKey="val"
                          placeholder="typ"
                          label="Typ"
                          className="col-md-3"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="login"
                          autocomplete="oldSelectedUserLogin"
                          placeholder="Váš prihlasovací údaj"
                          label="Login"
                          className="col-md-3"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="password"
                          type="password"
                          autocomplete="oldSelectedUserPassword"
                          placeholder="Vyplňte pre zmenu hesla"
                          label="Nové heslo"
                          className="col-md-3"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="password_confirmation"
                          type="password"
                          placeholder="Vyplňte pre zmenu hesla"
                          label="Zopakovať heslo"
                          className="col-md-3"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="new_role_id"
                          type="select"
                          options={rolesList.filter((g) => g.type === AppRoleTypes.global)}
                          optionLabel="title"
                          placeholder="rola"
                          label="Rola"
                          className="col-md-3"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="locale"
                          type="select"
                          options={Object.values(LocaleList)}
                          optionLabel="title"
                          optionKey="val"
                          placeholder="Jazyk"
                          label="Jazyk"
                          className="col-md-3"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="timezone"
                          type="select"
                          options={timezonesList}
                          optionLabel="title"
                          optionKey="id"
                          placeholder="Časová zóna"
                          label="Časová zóna"
                          className="col-md-3"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="nationality"
                          type="select"
                          options={countriesList}
                          optionLabel="title"
                          optionKey="code"
                          placeholder="Státna príslušnosť"
                          label="Státna príslušnosť"
                          className="col-md-3"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <hr/>

                        <FormGroup
                          name="title_before"
                          placeholder="titul"
                          label="Titul pred menom"
                          className="col-md-3"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="first_name"
                          placeholder="meno"
                          label="Meno"
                          className="col-md-3"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="last_name"
                          placeholder="priezvisko"
                          label="Priezvisko"
                          className="col-md-3"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="title_after"
                          placeholder="titul"
                          label="Titul za menom"
                          className="col-md-3"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="birth_number"
                          placeholder="Rodné číslo"
                          label="Rodné číslo"
                          className="col-md-3"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="birth_at"
                          type="date"
                          placeholder="Dátum narodenia"
                          label="Dátum narodenia"
                          className="col-md-3"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          type="textarea"
                          name="note"
                          placeholder="Poznámka"
                          label="Poznámka"
                          className="col-md-6"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <div className="clearfix"></div>

                        <hr/>

                        <div className="clearfix"></div>

                        <div className="col-sm-12 col-md-7">
                          <h5>Skupina</h5>
                          <p className='text-muted'>Priradte užívateľa do skupiny ako správcu, ktorý má na starosti celú
                            skupinu,
                            alebo iba ako bežný účet. Ak nenájdete skupinu, môžete <span
                              className="clickable link-primary text-decoration-underline"
                              onClick={() => onAddEditUserGroupAction()}>vytvoriť novú</span>.
                          </p>
                          <UserGroupsSelect userGroupsList={userGroupsList} groupRolesList={rolesList}
                                            selectedUserGroup={syncUserGroup}
                                            onChange={(gs) => onUserGroupSelectChange(gs)}/>
                        </div>

                        {!model?.id && <div className="col-sm-12">
                          <div className="text-center alert alert-info my-2">Dalšie informácie ako adresa a kontakty sa
                            dajú doplniť po vytvorení používateľa.
                          </div>
                        </div>}
                      </div>
                    </Tab>

                    <Tab eventKey="company" title="Spoločnosť">
                      <div className="row">
                        <FormGroup
                          name="company"
                          placeholder="Názov spoločnosti"
                          label="Spoločnosť"
                          className="col-md-4"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="company_number"
                          placeholder="IČO"
                          label="IČO"
                          className="col-md-4"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="tax_id"
                          placeholder="DIČ"
                          label="DIČ"
                          className="col-md-4"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="dph_id"
                          placeholder="IČDPH"
                          label="IČDPH"
                          className="col-md-4"
                          touched={touched} errors={errors} schema={formSchema}
                        />

                        <FormGroup
                          name="company_registration"
                          placeholder="Zapísane v ..."
                          label="Registrácia"
                          className="col-md-4"
                          touched={touched} errors={errors} schema={formSchema}
                        />
                      </div>
                    </Tab>

                    <Tab eventKey="documents" title="Dokumenty">
                      <div className="row">
                        <div className="col-sm-12">
                          <h5>Dokumenty</h5>
                          <p className='text-muted'>Pridajte dokumenty ku užívatelovi, prípadne dokumenty ku zmene
                            údajov.</p>
                          <Fileupload
                            attachmentTypes={Object.values(UserAttachmentTypes)}
                            attachmentCategories={allowedAttachmentCategories}
                            cardClass="col-sm-12"
                            files={files}
                            filesData={filesData}
                            onChange={({files, filesData}) => {
                              setFiles(files);
                              setFilesData(filesData)
                            }}
                            setFieldValue={setFieldValue}/>
                          {model?.attachments && model?.attachments?.length > 0 && <div className="row">
                            {model.attachments.map((att) => {
                              return <div key={att.id} className="col-12">
                                <Attachment attachment={att}
                                            onDelSuccess={(att) => onAttachmentDelete(att)}
                                            editAction={(att) => addEditAttachmentModal.current.openModal(att, Object.values(UserAttachmentTypes), allowedAttachmentCategories)}
                                /></div>;
                            })}
                          </div>}
                        </div>
                      </div>
                    </Tab>

                    <Tab disabled={!model?.id} eventKey="contacts" title="Kontakty">
                      <div className="row">
                        <div className="col-sm-12 col-md-6">
                          <h5>Adresy</h5>
                          {!model?.addresses?.length > 0 &&
                            <p className='text-muted'>Pridajte adresy ku používateľovi. Prvú adresu môžete <span
                              className="clickable link-primary text-decoration-underline"
                              onClick={() => addEditAddressModal.current.openModal(null, "App\\Models\\User", model.id)}>vytvoriť tu</span>.
                            </p>}
                          {model?.addresses?.length > 0 && <div className="row">{model.addresses?.map((address) => {
                            return (<div className="col-md-12" key={address.id}>
                              <AddressCell className="col-sm-12"
                                           key={address.id}
                                           address={address}
                                           edit={() => addEditAddressModal.current.openModal(address, "App\\Models\\User", model.id)}
                                           del={() => delAddress(address)}
                                           onSuccess={(c) => onAddEditAddressCallback(c)}/>
                            </div>)
                          })}
                            <br/>
                            <div className="text-center mt-2">
                              <button type="button" className='btn btn-outline-secondary'
                                      onClick={() => addEditAddressModal.current.openModal(null, "App\\Models\\User", model.id)}>
                                <span className="mdi mdi-plus-circle"></span> Pridať
                              </button>
                            </div>
                          </div>}
                        </div>

                        <div className="col-sm-12 col-md-6">
                          <h5>Kontakty</h5>
                          {!model?.contacts?.length > 0 &&
                            <p className='text-muted'>Pridajte kontakty ku používateľovi. Prvý kontakt môžete <span
                              className="clickable link-primary text-decoration-underline"
                              onClick={() => addEditContactModal.current.openModal(null, "App\\Models\\User", model.id)}>vytvoriť tu</span>.
                            </p>}
                          {model?.contacts?.length > 0 && <div className="row">{model.contacts?.map((contact) => {
                            return (<div className="col-md-12" key={contact.id}>
                              <ContactCell className="col-sm-12"
                                           key={contact.id}
                                           contact={contact}
                                           edit={() => addEditContactModal.current.openModal(contact, "App\\Models\\User", model.id)}
                                           del={() => delContact(contact)}
                                           onSuccess={(c) => onAddEditContactCallback(c)}/>
                            </div>)
                          })}
                            <br/>
                            <div className="text-center mt-2">
                              <button type="button" className='btn btn-outline-secondary'
                                      onClick={() => addEditContactModal.current.openModal(null, "App\\Models\\User", model.id)}>
                                <span className="mdi mdi-plus-circle"></span> Pridať
                              </button>
                            </div>
                          </div>}
                        </div>

                        <div className="col-sm-12 col-md-6">
                          <h5>Bankové účty</h5>
                          {!model?.bank_accounts?.length > 0 &&
                            <p className='text-muted'>Pridajte bankové účty k používateľovi. Prvý bankový účet
                              môžete <span className="clickable link-primary text-decoration-underline"
                                           onClick={() => addEditBankAccountModal.current.openModal(null, model.id)}>vytvoriť tu</span>.
                            </p>}
                          {model?.bank_accounts?.length > 0 &&
                            <div className="row">{model.bank_accounts?.map((bankAccount) => {
                              return (<div className="col-md-12" key={bankAccount.id}>
                                <BankAccountCell className="col-sm-12"
                                                 key={bankAccount.id}
                                                 bankAccount={bankAccount}
                                                 edit={() => addEditBankAccountModal.current.openModal(bankAccount, model.id)}
                                                 del={() => delBankAccount(bankAccount)}
                                                 onSuccess={(c) => onAddEditBankAccountCallback(c)}/>
                              </div>)
                            })}
                              <br/>
                              <div className="text-center mt-2">
                                <button type="button" className='btn btn-outline-secondary'
                                        onClick={() => addEditBankAccountModal.current.openModal(null, model.id)}><span
                                  className="mdi mdi-plus-circle"></span> Pridať
                                </button>
                              </div>
                            </div>}
                        </div>
                      </div>
                    </Tab>

                    <Tab disabled={!model?.id} eventKey="delegates" title="Delegáti">
                      <div className="row">
                        <div className="col-sm-12">
                          <h5>Delegáti</h5>
                          {!model?.user_representatives?.length > 0 &&
                            <p className='text-muted'>Pridajte delegáta k používateľovi. Prvého delegáta môžete <span
                              className="clickable link-primary text-decoration-underline"
                              onClick={() => addEditUserRepresentativeModal.current.openModal(null, model.id)}>vytvoriť tu</span>.
                            </p>}
                          {model?.user_representatives?.length > 0 &&
                            <div className="row">{model.user_representatives?.map((userRepresentative) => {
                              return (<div className="col-md-12" key={userRepresentative.id}>
                                <UserRepresentativeCell className="col-sm-12"
                                                        key={userRepresentative.id}
                                                        userRepresentative={userRepresentative}
                                                        edit={() => addEditUserRepresentativeModal.current.openModal(userRepresentative, model.id, model.type)}
                                                        del={() => delUserRepresentative(userRepresentative)}
                                                        onSuccess={(c) => onAddEditUserRepresentativeCallback(c)}/>
                              </div>)
                            })}
                              <br/>
                              <div className="text-center mt-2">
                                <button type="button" className='btn btn-outline-secondary'
                                        onClick={() => addEditUserRepresentativeModal.current.openModal(null, model.id, model.type)}>
                                  <span className="mdi mdi-plus-circle"></span> Pridať
                                </button>
                              </div>
                            </div>}
                        </div>
                      </div>
                    </Tab>

                  </Tabs>

                </Form>}

              </Section>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="outline-secondary" onClick={closeModal}>Zatvoriť</Button>
              <Button type="submit" variant="primary" onClick={handleSubmit} disabled={isSubmitting}>Odoslať</Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>

    </>
  );
});

export default AddEditUserModal;
